/*
 * ---------------------------------------------------------------------------
 * IMPORT CONFIGURATION (frontend single source of truth)
 * ---------------------------------------------------------------------------
 * One entry per master-data import. This drives BOTH modals (mapping + import)
 * and the service calls, so adding a new import type later means adding one
 * block here — no new modal/component files.
 *
 *   key         -> internal identifier
 *   title       -> shown in modal headers / cards
 *   urlType     -> the :type slug in the backend route  /api/imports/:type
 *                  (MUST match a key in the backend IMPORT_CONFIGS)
 *   importType  -> the import_type tag stored on the mapping profile, and used
 *                  to filter which profiles show up for this import
 *   columns     -> the DB columns offered in the mapping modal. `required`
 *                  ones must be mapped before a profile can be saved.
 *
 * The `columns` lists mirror the md_* tables in your schema. `required` here
 * matches the NOT NULL columns that have no safe default on the backend.
 * ---------------------------------------------------------------------------
 */

export const IMPORT_CONFIGS = {
  gl_accounts: {
    key: 'gl_accounts',
    title: 'GL Accounts',
    urlType: 'gl-accounts',
    importType: 'GL_ACCOUNT',
    keyColumn: 'account_code',
    columns: [
      { id: 'account_code',          label: 'Account Code',                    required: true },
      { id: 'account_description',   label: 'Account Description',             required: true },
      { id: 'account_category',      label: 'Account Category',               required: true,  allowed: ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'] },
      { id: 'normal_balance',        label: 'Normal Balance',                 required: true,  allowed: ['Debit', 'Credit'] },
      { id: 'default_cost_center',   label: 'Default Cost Center',            required: false },
      { id: 'parent_account_code',   label: 'Parent Account Code',           required: false },
      // Drives the P&L layout. Blank = unclassified; the P&L falls back by
      // category and names the account rather than guessing quietly.
      { id: 'pnl_section',           label: 'P&L Section',                    required: false, allowed: ['GROSS_REVENUE', 'DISCOUNTS', 'COGS', 'DISTRIBUTION', 'OVERHEADS', 'DEPRECIATION', 'OTHER_INCOME', 'FINANCE_COSTS', 'TAX', 'NON_PL'] },
      { id: 'account_group',         label: 'Account Group',                  required: false },
      { id: 'is_active',             label: 'Is Active (Boolean)',           required: false },
      { id: 'allow_manual_journals', label: 'Allow Manual Journals (Boolean)', required: false },
      { id: 'is_control_account',    label: 'Is Control Account (Boolean)',  required: false },
      { id: 'default_currency',      label: 'Default Currency',              required: false },
    ],
  },

  stock_codes: {
    key: 'stock_codes',
    title: 'Stock Codes',
    urlType: 'stock-codes',
    importType: 'STOCK_CODE',
    keyColumn: 'stock_code',
    columns: [
      { id: 'stock_code',        label: 'Stock Code',                 required: true },
      { id: 'description',       label: 'Description',                required: true },
      { id: 'unit_of_measure',   label: 'Unit of Measure',           required: true },
      // Factor = how many BASE units make ONE alt unit (120 eaches = 1 pallet).
      // Volume-linked cost drivers divide by it, so a mixed-UoM volume plan can
      // be summed into pallets or tonnes.
      { id: 'alt_uom_1',         label: 'Alt UoM 1',                 required: false },
      { id: 'alt_uom_1_factor',  label: 'Base Units per Alt UoM 1',  required: false },
      { id: 'alt_uom_2',         label: 'Alt UoM 2',                 required: false },
      { id: 'alt_uom_2_factor',  label: 'Base Units per Alt UoM 2',  required: false },
      { id: 'long_description',  label: 'Long Description',          required: false },
      { id: 'product_class',     label: 'Product Class',             required: false },
      { id: 'stock_group',       label: 'Stock Group',               required: false },
      { id: 'default_warehouse', label: 'Default Warehouse',         required: false },
      { id: 'valuation_method',  label: 'Valuation Method (Enum)',   required: false, allowed: ['FIFO', 'LIFO', 'Standard', 'Average', 'Specific'] },
      { id: 'tax_code',          label: 'Tax Code',                  required: false },
      { id: 'is_active',         label: 'Is Active (Boolean)',       required: false },
      { id: 'is_stock_item',     label: 'Is Stock Item (Boolean)',   required: false },
      { id: 'procurement_item',  label: 'Procurement Item (Boolean)', required: false },
    ],
  },

  cost_centers: {
    key: 'cost_centers',
    title: 'Cost Centres',
    urlType: 'cost-centers',
    importType: 'COST_CENTER',
    keyColumn: 'cost_center_code',
    columns: [
      { id: 'cost_center_code',   label: 'Cost Centre Code',   required: true },
      { id: 'description',        label: 'Description',        required: true },
      { id: 'parent_cost_center', label: 'Parent Cost Centre', required: false },
      { id: 'is_active',          label: 'Is Active (Boolean)', required: false },
    ],
  },

  sites: {
    key: 'sites',
    title: 'Sites',
    urlType: 'sites',
    importType: 'SITE',
    keyColumn: 'site_code',
    columns: [
      { id: 'site_code', label: 'Site Code',           required: true },
      { id: 'site_name', label: 'Site Name',           required: true },
      { id: 'region',    label: 'Region',              required: false },
      { id: 'is_active', label: 'Is Active (Boolean)', required: false },
    ],
  },

  warehouses: {
    key: 'warehouses',
    title: 'Warehouses',
    urlType: 'warehouses',
    importType: 'WAREHOUSE',
    keyColumn: 'warehouse_id',
    columns: [
      { id: 'warehouse_id', label: 'Warehouse ID',        required: true },
      { id: 'description',  label: 'Description',          required: true },
      { id: 'site_code',    label: 'Site Code',           required: false },
      { id: 'is_active',    label: 'Is Active (Boolean)', required: false },
    ],
  },

  cost_drivers: {
    key: 'cost_drivers',
    title: 'Cost Drivers',
    urlType: 'cost-drivers',
    importType: 'COST_DRIVER',
    keyColumn: 'driver_code',
    columns: [
      { id: 'driver_code',    label: 'Driver Code',             required: true },
      { id: 'description',    label: 'Description',             required: true },
      { id: 'driver_uom',     label: 'Unit of Measure',         required: false },
      { id: 'calc_method',    label: 'Calculation (Enum)',      required: false, allowed: ['RATE_X_QTY', 'PCT_OF_VALUE'] },
      { id: 'default_source', label: 'Default Source (Enum)',   required: false, allowed: ['MANUAL', 'PRODUCTION_VOLUME', 'SALES_VOLUME'] },
      { id: 'is_active',      label: 'Is Active (Boolean)',     required: false },
    ],
  },

  // NOTE: there is no `suppliers` entry here on purpose. Suppliers load through
  // the Master Data page's own Import / Export (Excel round-trip, see
  // utils/masterDataExcel.js) rather than this mapping-profile framework, so a
  // block here would be a second, unreachable way to do the same thing.

  stock_actuals: {
    key: 'stock_actuals',
    title: 'Stock Actuals',
    urlType: 'stock-actuals',
    importType: 'STOCK_ACTUAL',
    // Composite key: all four must be mapped before a profile can be saved.
    columns: [
      { id: 'warehouse_id',     label: 'Warehouse ID',     required: true },
      { id: 'stock_code',       label: 'Stock Code',       required: true },
      { id: 'financial_year',   label: 'Financial Year',   required: true },
      { id: 'financial_period', label: 'Financial Period', required: true },
      { id: 'cost_amount',      label: 'Cost Amount',      required: false },
    ],
  },

  bom_actuals: {
    key: 'bom_actuals',
    title: 'BOM Actuals',
    urlType: 'bom-actuals',
    importType: 'BOM_ACTUAL',
    // Composite key: year + site + parent + component must all be mapped.
    columns: [
      { id: 'financial_year', label: 'Financial Year',       required: true },
      { id: 'site_code',      label: 'Site Code',            required: true },
      { id: 'parent_code',    label: 'Parent Code (Item)',   required: true },
      { id: 'component_code', label: 'Component Code',       required: true },
      { id: 'qty_per',        label: 'Qty Per',              required: false },
      { id: 'scrap_pct',      label: 'Scrap %',              required: false },
      { id: 'fixed_loss',     label: 'Fixed Loss',           required: false },
    ],
  },
};

export default IMPORT_CONFIGS;